import React from 'react';
import { Check, Circle } from 'lucide-react';

/**
 * Horizontal status pipeline (Pipedrive/HubSpot style) to show the document's progress.
 *
 * Props:
 *  - steps: [{ key, label, ts?, clickable?, onClick? }]
 *    - key: unique identifier matching the current status
 *    - label: visible label (short)
 *    - ts: ISO datetime of when the step was reached (optional)
 *    - clickable: boolean (optional) - whether user can click to advance to this step
 *    - onClick: fn (optional) - invoked when user clicks this step
 *  - currentKey: key of the currently active step
 *  - tone: 'purple' | 'teal' | 'blue' (default 'purple')
 *  - canceled: boolean (optional) - if true, render the pipeline in a muted/canceled state
 */
export default function StatusPipeline({ steps, currentKey, tone = 'purple', canceled = false }) {
  const currentIdx = steps.findIndex((s) => s.key === currentKey);
  const toneColor = {
    purple: { bg: '#4F46E5', bgSoft: '#EEF2FF', text: '#4F46E5', line: '#C7D2FE' },
    teal: { bg: '#0F766E', bgSoft: '#E6FBF7', text: '#0F766E', line: '#99F6E4' },
    blue: { bg: '#0369A1', bgSoft: '#E0F2FE', text: '#0369A1', line: '#BAE6FD' },
  }[tone] || { bg: '#4F46E5', bgSoft: '#EEF2FF', text: '#4F46E5', line: '#C7D2FE' };

  return (
    <div data-testid="status-pipeline" className={`flex items-stretch ${canceled ? 'opacity-40' : ''}`}>
      {steps.map((s, i) => {
        const done = !canceled && currentIdx >= 0 && i < currentIdx;
        const active = !canceled && i === currentIdx;
        const pending = canceled ? false : i > currentIdx;
        const clickable = s.clickable && s.onClick && !active && !canceled;
        const bg = done || active ? toneColor.bg : '#E5E7EB';
        const circleBg = done ? toneColor.bg : active ? '#fff' : '#F3F4F8';
        const borderCol = done || active ? toneColor.bg : clickable ? toneColor.bg : '#E5E7EB';
        const labelColor = active ? toneColor.text : pending ? (clickable ? toneColor.text : '#9CA3AF') : '#1A1B2E';
        const borderStyle = clickable ? 'dashed' : 'solid';

        const Wrap = clickable ? 'button' : 'div';
        const wrapProps = clickable
          ? {
              onClick: s.onClick,
              type: 'button',
              'data-testid': `pipeline-step-${s.key}`,
              title: s.hint || `Passer à l'étape « ${s.label} »`,
              className: 'flex-1 min-w-0 text-left hover:scale-[1.04] hover:opacity-100 opacity-90 transition-all cursor-pointer',
            }
          : { 'data-testid': `pipeline-step-${s.key}`, className: 'flex-1 min-w-0' };

        return (
          <React.Fragment key={s.key}>
            <Wrap {...wrapProps}>
              <div className="flex flex-col items-center">
                <div
                  className="w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0"
                  style={{ background: circleBg, borderColor: borderCol, borderStyle }}
                >
                  {done ? (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  ) : active ? (
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: toneColor.bg }} />
                  ) : (
                    <Circle className="w-2.5 h-2.5 text-[#9CA3AF]" />
                  )}
                </div>
                <div className="mt-1 text-center px-0.5 w-full">
                  <div className="text-[10px] font-semibold uppercase tracking-tight leading-tight" style={{ color: labelColor, wordBreak: 'normal' }}>
                    {s.label}
                  </div>
                  {s.ts && (
                    <div className="text-[9.5px] text-[#8E8FA5] mt-0.5">
                      {new Date(s.ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    </div>
                  )}
                </div>
              </div>
            </Wrap>
            {i < steps.length - 1 && (
              <div className="flex-shrink-0 pt-3 px-0.5">
                <div
                  className="h-0.5 w-6 md:w-10 rounded-full"
                  style={{ background: done || (active && i < currentIdx) ? bg : toneColor.line }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
